import React, { useState, useMemo } from 'react'
import { Container, Top } from '../styles'
import { DefaultAdminPage } from '../../DefaultPages'
import {
  Wrap,
  BoxGraphic,
  GrossRevenue,
  BoxTotal,
  Icon,
  IcoTotal
} from './styles'

import {
  BoxContainer,
  InsideFooter,
  BoxDash,
  Breadcrumbs,
  CircleLoading,
  DiscontinuousScale
} from '../../../components'
import { useQuery } from 'react-query'
import ecbrApi from '../../../services/ecbrApi'

import { numberToCurrency } from '../../../utils/numberToCurrency'
import { dateTypes } from '../../../constants/date'
import DashBoardFilters from './DashBoardFilters'
import { centsToReal } from '../../../helpers/centsToReal'
import { fixNaN } from '../../../helpers/fixNaN'
import dayjs from '../../../helpers/dayjs'
import { formatToPercentage } from '../../../helpers/formatToPercentage'

// Example of payload for dashboard page:
/*
const payload = {
  totalCompanies: 0,
  grossRevenue: [
    {
      year: '2022',
      month: '01',
      totalAmount: 5000,
      totalInvoices: 5
    },
    {
      year: '2022',
      month: '02',
      totalAmount: 7000,
      totalInvoices: 4
    },
  ],
  certificatesPendings: 0,
  certificatesActives: 0
}
*/

const Dashboard = () => {
  const [_error, setError] = useState('')
  const [searchParameters, setSearchParameters] = useState({
    start_date: dayjs().subtract(1, 'month').format(dateTypes.startDate),
    end_date: dayjs().format(dateTypes.endDate)
  })
  const [loadingSearch, setLoadingSearch] = useState(true)
  const [loadingFullPeriod, setLoadingFullPeriod] = useState(true)
  const [loadingLastMonth, setLoadingLastMonth] = useState(true)

  const { data: dashboardsSearch } = useQuery(
    ['dashboard', searchParameters],
    async ({ queryKey: [_, params] }) => {
      try {
        setLoadingSearch(true)
        const resp = await ecbrApi.adminDashboards.get(params)

        if (!resp.data) return undefined

        const data = {
          ...resp.data,
          grossRevenue: resp.data.grossRevenue.map((item) => ({
            ...item,
            totalAmount: centsToReal(item.totalAmount)
          }))
        }

        setLoadingSearch(false)
        return data
      } catch (error) {
        setLoadingSearch(false)
        setError(error.message)
      }
    },
    {
      cacheTime: 0,
      onSuccess: () => {
        setError(false)
      },
      onError: () => {
        setError('Não foi possível carregar os dados')
      }
    }
  )

  const { data: dashboardsFullPeriod } = useQuery(
    ['dashboard_full_period', searchParameters],
    async ({ queryKey: [_, params] }) => {
      try {
        setLoadingFullPeriod(true)
        const resp = await ecbrApi.adminDashboards.getFullPeriod(
          params.end_date
        )

        if (!resp.data) return undefined

        const data = {
          ...resp.data,
          grossRevenue: resp.data.grossRevenue.map((item) => ({
            ...item,
            totalAmount: centsToReal(item.totalAmount)
          }))
        }

        setLoadingFullPeriod(false)
        return data
      } catch (error) {
        setLoadingFullPeriod(false)
      }
    },
    {
      cacheTime: 0,
      onSuccess: () => {
        setError(false)
      },
      onError: () => {
        setError('Não foi possível carregar os dados')
      }
    }
  )

  const { data: dashboardsLastMonth } = useQuery(
    ['dashboard_last_month', searchParameters],
    async ({ queryKey: [_, params] }) => {
      try {
        setLoadingLastMonth(true)
        const resp = await ecbrApi.adminDashboards.get({
          start_date: dayjs(params.start_date, dateTypes.startDate)
            .subtract(2, 'month')
            .format(dateTypes.startDate),
          end_date: dayjs(params.end_date, dateTypes.endDate)
            .subtract(1, 'month')
            .format(dateTypes.endDate)
        })

        if (!resp.data) return undefined

        const data = {
          ...resp.data,
          grossRevenue: resp.data.grossRevenue.map((item) => ({
            ...item,
            totalAmount: centsToReal(item.totalAmount)
          }))
        }

        setLoadingLastMonth(false)
        return data
      } catch (error) {
        setLoadingLastMonth(false)
      }
    },
    {
      cacheTime: 0,
      onSuccess: () => {
        setError(false)
      },
      onError: () => {
        setError('Não foi possível carregar os dados')
      }
    }
  )

  const dashboards = useMemo(
    () => [
      {
        title: 'Empresas Cadastradas',
        startDate: dayjs(
          searchParameters.start_date,
          dateTypes.dateTimeISO
        ).format(dateTypes.dateShortBR),
        endDate: dayjs(searchParameters.end_date, dateTypes.dateTimeISO).format(
          dateTypes.dateShortBR
        ),
        quantity: dashboardsSearch?.totalCompanies,
        percent: `${fixNaN(
          (dashboardsSearch?.totalCompanies /
            dashboardsFullPeriod?.totalCompanies) *
            100
        ).toFixed(2)}%`,
        // up will be true if quantity is greater than last month
        up:
          dashboardsSearch?.totalCompanies > dashboardsLastMonth?.totalCompanies
      },
      {
        title: 'Empresas Certificadas',
        startDate: dayjs(
          searchParameters.start_date,
          dateTypes.startDate
        ).format(dateTypes.dateShortBR),
        endDate: dayjs(searchParameters.end_date, dateTypes.endDate).format(
          dateTypes.dateShortBR
        ),
        quantity: dashboardsSearch?.certificatesActives,
        percent: `${fixNaN(
          (dashboardsSearch?.certificatesActives /
            dashboardsFullPeriod?.certificatesActives) *
            100
        ).toFixed(2)}%`,
        up:
          dashboardsSearch?.certificatesActives >
          dashboardsLastMonth?.certificatesActives
      },
      {
        title: 'Certificações em andamento',
        startDate: dayjs(
          searchParameters.start_date,
          dateTypes.startDate
        ).format(dateTypes.dateShortBR),
        endDate: dayjs(searchParameters.end_date, dateTypes.endDate).format(
          dateTypes.dateShortBR
        ),
        quantity: dashboardsSearch?.certificatesPendings,
        percent: `${fixNaN(
          (dashboardsSearch?.certificatesPendings /
            dashboardsFullPeriod?.certificatesPendings) *
            100
        ).toFixed(2)}%`,
        up:
          dashboardsSearch?.certificatesPendings >
          dashboardsLastMonth?.certificatesPendings
      }
    ],
    [
      dashboardsSearch,
      dashboardsFullPeriod,
      dashboardsLastMonth,
      searchParameters
    ]
  )

  const chartScale = useMemo(
    () =>
      dashboardsSearch?.grossRevenue.map(({ year, month, totalAmount }) => ({
        x: `${month}/${year}`,
        y: numberToCurrency(totalAmount)
      })),
    [dashboardsSearch]
  )

  const sumAmountSearch = useMemo(
    () =>
      dashboardsSearch?.grossRevenue.reduce(
        (acc, { totalAmount }) => acc + totalAmount,
        0
      ),
    [dashboardsSearch]
  )

  const sumAmountLastMonth = useMemo(
    () =>
      dashboardsLastMonth?.grossRevenue.reduce(
        (acc, { totalAmount }) => acc + totalAmount,
        0
      ),
    [dashboardsLastMonth]
  )

  const percentageIncreaseSinceLastMonth = useMemo(() => {
    const calc =
      ((sumAmountSearch - sumAmountLastMonth) / sumAmountLastMonth) * 100

    if (calc === Infinity && sumAmountSearch > 0) {
      return 100
    }

    if (calc === Infinity) {
      return 0
    }

    return calc.toFixed(2)
  }, [sumAmountSearch, sumAmountLastMonth])

  return (
    <DefaultAdminPage page="dashboard">
      <Container>
        <Wrap>
          <Top>
            <Breadcrumbs />
            {/* <Button
              type="button"
              variant="outlined"
              text="Exportar excel"
              width="120px"
            /> */}
          </Top>
          <Top className="spaceTop">
            <h2>Visão Geral</h2>
          </Top>

          <DashBoardFilters
            filters={searchParameters}
            setFilters={setSearchParameters}
            isLoading={loadingSearch || loadingFullPeriod || loadingLastMonth}
          />

          <BoxContainer>
            {dashboards &&
              dashboards.map((element, index) => {
                return <BoxDash data={element} key={index} />
              })}
          </BoxContainer>

          <Top className="spaceTop">
            <h2>Gráficos</h2>
          </Top>

          <BoxGraphic>
            <GrossRevenue>
              <h3>Receita Bruta</h3>
              <DiscontinuousScale
                dataScale={chartScale?.length ? chartScale : [{ x: '0', y: 1 }]}
              />
              {/* <AdminChart data={chartScale} /> */}
              <BoxTotal>
                <h4>Total</h4>
                <h5>{numberToCurrency(sumAmountSearch)}</h5>
                <IcoTotal positive={true}>
                  <Icon />
                  {loadingSearch || loadingLastMonth ? (
                    <CircleLoading />
                  ) : (
                    <span>
                      {formatToPercentage(percentageIncreaseSinceLastMonth)}
                    </span>
                  )}
                </IcoTotal>
              </BoxTotal>
            </GrossRevenue>

            {/* <Conversion>
              <h3>Conversão</h3>
              <PieChart dataPie={dataPie} />
            </Conversion> */}
          </BoxGraphic>
        </Wrap>
        <InsideFooter />
      </Container>
    </DefaultAdminPage>
  )
}

export default Dashboard
